'use client';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { type AppThemeMode, createAppTheme } from '@/theme';

type ThemePreference = 'auto' | AppThemeMode;

type ThemeModeContextValue = {
  preference: ThemePreference;
  resolvedMode: AppThemeMode;
  setPreference: (preference: ThemePreference) => void;
  toggleMode: () => void;
};

const THEME_PREFERENCE_COOKIE_NAME = 'freedraw-theme-preference';
const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function getSystemMode(): AppThemeMode {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readSavedPreference(): ThemePreference | null {
  if (typeof document === 'undefined') return null;
  const cookie = document.cookie.split('; ').find((part) => part.startsWith(`${THEME_PREFERENCE_COOKIE_NAME}=`));
  const value = cookie?.slice(THEME_PREFERENCE_COOKIE_NAME.length + 1);
  return value === 'light' || value === 'dark' || value === 'auto' ? value : null;
}

export function MuiAppProvider({
  children,
  initialPreference = 'auto'
}: {
  children: React.ReactNode;
  initialPreference?: ThemePreference;
}) {
  // Public landing/login pages explicitly request light to preserve their editorial palette.
  // The board uses auto and restores the visitor's saved light/dark choice.
  const fixedLight = initialPreference === 'light';
  const [preference, setPreferenceState] = useState<ThemePreference>(initialPreference);
  const [systemMode, setSystemMode] = useState<AppThemeMode>('light');

  useEffect(() => {
    if (fixedLight) return;
    setSystemMode(getSystemMode());
    const stored = readSavedPreference();
    if (stored) setPreferenceState(stored);
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemMode(media.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [fixedLight]);

  const setPreference = useCallback((next: ThemePreference) => {
    if (fixedLight) return;
    setPreferenceState(next);
    document.cookie = `${THEME_PREFERENCE_COOKIE_NAME}=${next}; Path=/; Max-Age=31536000; SameSite=Lax`;
  }, [fixedLight]);

  const resolvedMode: AppThemeMode = fixedLight ? 'light' : preference === 'auto' ? systemMode : preference;
  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  useEffect(() => {
    document.documentElement.dataset.colorScheme = resolvedMode;
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  const toggleMode = useCallback(() => {
    setPreference(resolvedMode === 'dark' ? 'light' : 'dark');
  }, [resolvedMode, setPreference]);

  const contextValue = useMemo<ThemeModeContextValue>(
    () => ({ preference, resolvedMode, setPreference, toggleMode }),
    [preference, resolvedMode, setPreference, toggleMode]
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useAppThemeMode() {
  const context = useContext(ThemeModeContext);
  if (!context) throw new Error('useAppThemeMode must be used within MuiAppProvider');
  return context;
}
